import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  Modal,
  Progress,
  Row,
  Segmented,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message as antdMessage,
} from 'antd';
import {
  CheckCircleTwoTone,
  ClockCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  MessageOutlined,
  ReloadOutlined,
  RollbackOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  addCustomerSupportLog,
  fetchCustomerSupportLogs,
  fetchOnboardingAlerts,
  resolveOnboardingStage,
  undoOnboardingResolution,
} from '../services/onboardingAlertsApi';

const { Title, Text, Paragraph } = Typography;

const STAGE_META = {
  stage1: {
    key: 'no_group',
    title: 'Sem sincronizar grupo',
    emoji: '🚩',
    color: '#ff4d4f',
    bg: '#fff1f0',
    description: 'Time se cadastrou e ainda não vinculou grupo no WhatsApp.',
    metric: 'days_since_created',
    metricLabel: 'Cadastrado há',
    metricUnit: 'd',
  },
  stage2: {
    key: 'no_players',
    title: 'Sem jogadores',
    emoji: '⚙️',
    color: '#fa8c16',
    bg: '#fff7e6',
    description: 'Grupo sincronizado, mas nenhum jogador cadastrado ainda.',
    metric: 'days_since_created',
    metricLabel: 'Cadastrado há',
    metricUnit: 'd',
  },
  stage3: {
    key: 'no_game',
    title: 'Sem gerar jogo',
    emoji: '🎯',
    color: '#faad14',
    bg: '#fffbe6',
    description: 'Tem jogador cadastrado, mas nunca gerou nenhum jogo.',
    metric: 'days_since_first_player',
    metricLabel: 'Primeiro jogador há',
    metricUnit: 'd',
  },
  stage4: {
    key: 'no_call_sent',
    title: 'Jogo aberto sem convocação',
    emoji: '📵',
    color: '#722ed1',
    bg: '#f9f0ff',
    description: 'Jogo criado mas nenhuma chamada foi enviada.',
    metric: 'hours_since_game',
    metricLabel: 'Jogo criado há',
    metricUnit: 'h',
  },
};

const OnboardingAlerts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('todos');
  const [searchText, setSearchText] = useState('');
  const [resolveModal, setResolveModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchOnboardingAlerts();
      setData(res);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stages = useMemo(() => {
    if (!data) return [];
    return [
      { id: 'stage1', ...STAGE_META.stage1, rows: data.stage1 || [] },
      { id: 'stage2', ...STAGE_META.stage2, rows: data.stage2 || [] },
      { id: 'stage3', ...STAGE_META.stage3, rows: data.stage3 || [] },
      { id: 'stage4', ...STAGE_META.stage4, rows: data.stage4 || [] },
    ];
  }, [data]);

  return (
    <div style={{ padding: 24, background: '#f5f5f7', minHeight: 'calc(100vh - 64px)' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            🎯 Onboarding · Times travados
          </Title>
          <Text type="secondary">
            Atualizado{' '}
            {data?.generated_at
              ? new Date(data.generated_at).toLocaleString('pt-BR', {
                  timeZone: 'America/Sao_Paulo',
                })
              : '—'}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Atualizar
            </Button>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert type="error" showIcon message="Falha ao carregar" description={error} style={{ marginBottom: 16 }} />
      )}

      <FunnelHeader funnel={data?.funnel} loading={loading} />

      <StageCards stages={stages} counts={data?.counts} loading={loading} onTabChange={setTab} activeTab={tab} />

      <Card style={{ marginTop: 16 }} bodyStyle={{ padding: 0 }}>
        <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0' }}>
          <Row gutter={16} justify="space-between" align="middle">
            <Col flex="auto">
              <Segmented
                value={tab}
                onChange={setTab}
                options={[
                  {
                    label: (
                      <Space>
                        <span>Todos</span>
                        <Badge count={data?.counts?.total || 0} showZero style={{ backgroundColor: '#8c8c8c' }} />
                      </Space>
                    ),
                    value: 'todos',
                  },
                  ...stages.map((s) => ({
                    label: (
                      <Space>
                        <span>{s.emoji}</span>
                        <span>{s.title}</span>
                        <Badge count={s.rows.length} showZero style={{ backgroundColor: s.color }} />
                      </Space>
                    ),
                    value: s.id,
                  })),
                ]}
              />
            </Col>
            <Col>
              <Input.Search
                placeholder="Buscar time por nome"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260 }}
              />
            </Col>
          </Row>
        </div>

        {loading ? (
          <div style={{ padding: 24 }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : (
          <TeamsTable
            stages={stages}
            tab={tab}
            searchText={searchText}
            onResolve={(row, stage) => setResolveModal({ row, stage })}
            onUndo={async (resolutionId) => {
              try {
                await undoOnboardingResolution(resolutionId);
                antdMessage.success('Resolução desfeita');
                load();
              } catch (err) {
                antdMessage.error(err.response?.data?.message || 'Falha ao desfazer');
              }
            }}
            onOpenHistory={(row) => setHistoryModal(row)}
          />
        )}
      </Card>

      <ResolveModal
        open={!!resolveModal}
        row={resolveModal?.row}
        stage={resolveModal?.stage}
        onCancel={() => setResolveModal(null)}
        onDone={() => {
          setResolveModal(null);
          load();
        }}
      />

      <HistoryModal
        open={!!historyModal}
        customerId={historyModal?.customer_id}
        customerName={historyModal?.game_name}
        onClose={() => setHistoryModal(null)}
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Header com funil
// ────────────────────────────────────────────────────────

const FunnelHeader = ({ funnel, loading }) => {
  if (loading || !funnel) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    );
  }
  const total = Number(funnel.total || 0);
  const grupo = Number(funnel.com_grupo || 0);
  const player = Number(funnel.com_player || 0);
  const jogo = Number(funnel.com_jogo || 0);
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

  const steps = [
    { label: 'Ativos 90d', value: total, pct: 100, color: '#1677ff' },
    { label: 'Com grupo', value: grupo, pct: pct(grupo), color: '#52c41a' },
    { label: 'Com jogador', value: player, pct: pct(player), color: '#faad14' },
    { label: 'Com jogo', value: jogo, pct: pct(jogo), color: '#722ed1' },
  ];

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Funil de ativação (últimos 90 dias)</span>
        </Space>
      }
    >
      <Row gutter={16}>
        {steps.map((s) => (
          <Col xs={12} md={6} key={s.label}>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="dashboard"
                percent={s.pct}
                width={110}
                strokeColor={s.color}
                format={() => (
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>{s.pct}%</div>
                  </div>
                )}
              />
              <div style={{ marginTop: 8, fontWeight: 500 }}>{s.label}</div>
            </div>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

// ────────────────────────────────────────────────────────
// Cards por estágio
// ────────────────────────────────────────────────────────

const StageCards = ({ stages, counts, loading, onTabChange, activeTab }) => {
  return (
    <Row gutter={[16, 16]}>
      {stages.map((s) => (
        <Col xs={12} md={6} key={s.id}>
          <Card
            hoverable
            onClick={() => onTabChange(s.id)}
            style={{
              borderTop: `3px solid ${s.color}`,
              boxShadow: activeTab === s.id ? '0 6px 16px rgba(0,0,0,0.12)' : undefined,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            bodyStyle={{ padding: 20 }}
          >
            <Skeleton loading={loading} active paragraph={{ rows: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 32, marginBottom: 4 }}>{s.emoji}</div>
                  <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 4 }}>{s.title}</div>
                  <div style={{ fontSize: 34, fontWeight: 700, color: s.color, lineHeight: 1 }}>
                    {s.rows.length}
                  </div>
                </div>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {s.description}
              </Text>
            </Skeleton>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// ────────────────────────────────────────────────────────
// Tabela unificada
// ────────────────────────────────────────────────────────

const TeamsTable = ({ stages, tab, searchText, onResolve, onUndo, onOpenHistory }) => {
  const rows = useMemo(() => {
    let items = [];
    if (tab === 'todos') {
      stages.forEach((s) => {
        s.rows.forEach((r) => items.push({ ...r, __stage: s }));
      });
    } else {
      const s = stages.find((x) => x.id === tab);
      if (s) items = s.rows.map((r) => ({ ...r, __stage: s }));
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      items = items.filter(
        (i) =>
          String(i.customer_id).includes(q) ||
          (i.game_name || '').toLowerCase().includes(q),
      );
    }
    return items;
  }, [stages, tab, searchText]);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 40 }}>
        <Empty
          image={
            <CheckCircleTwoTone
              twoToneColor="#52c41a"
              style={{ fontSize: 64 }}
            />
          }
          imageStyle={{ height: 80 }}
          description={
            <div>
              <Title level={4}>Tudo em dia!</Title>
              <Text type="secondary">Nenhum time travado nesse filtro.</Text>
            </div>
          }
        />
      </div>
    );
  }

  const columns = [
    {
      title: 'Estágio',
      key: 'stage',
      width: 220,
      render: (_, row) => (
        <Tag color={row.__stage.color} style={{ padding: '2px 10px' }}>
          {row.__stage.emoji} {row.__stage.title}
        </Tag>
      ),
    },
    {
      title: 'Time',
      key: 'team',
      render: (_, row) => (
        <div>
          <Link to={`/customers/${row.customer_id}/ficha`}>
            <Text strong>{row.game_name || `#${row.customer_id}`}</Text>
          </Link>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              id {row.customer_id}
              {row.game_id ? ` · jogo #${row.game_id}` : ''}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Tempo travado',
      key: 'time',
      align: 'center',
      width: 160,
      render: (_, row) => {
        const meta = row.__stage;
        const raw = row[meta.metric];
        if (raw == null) return <Text type="secondary">—</Text>;
        const value = Math.floor(Number(raw));
        return (
          <Tooltip title={meta.metricLabel}>
            <Tag color="default" style={{ fontSize: 13, padding: '4px 10px' }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {value}
              {meta.metricUnit}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const av = a[a.__stage.metric] || 0;
        const bv = b[b.__stage.metric] || 0;
        return av - bv;
      },
    },
    {
      title: 'Suporte',
      key: 'support',
      width: 200,
      render: (_, row) => {
        if (!row.support_logs_count) {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        return (
          <div>
            <Space size={4}>
              <MessageOutlined style={{ color: '#1677ff' }} />
              <Text style={{ fontSize: 12 }}>{row.support_logs_count} entradas</Text>
            </Space>
            {row.last_support_author && (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  últ. {row.last_support_author}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_, row) => {
        if (row.resolution) {
          return (
            <Tag color="green">
              <CheckCircleTwoTone twoToneColor="#52c41a" /> Resolvido por{' '}
              {row.resolution.resolved_by_name}
            </Tag>
          );
        }
        return <Tag color="orange">Aberto</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 260,
      render: (_, row) => (
        <Space>
          <Tooltip title="Ver histórico">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => onOpenHistory(row)}
            />
          </Tooltip>
          <Tooltip title="Ver ficha do time">
            <Link to={`/customers/${row.customer_id}/ficha`}>
              <Button size="small" icon={<EyeOutlined />} />
            </Link>
          </Tooltip>
          {row.resolution ? (
            <Button
              size="small"
              icon={<UndoOutlined />}
              onClick={() => onUndo(row.resolution.id)}
            >
              Desfazer
            </Button>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleTwoTone twoToneColor="#fff" />}
              onClick={() => onResolve(row, row.__stage.key)}
            >
              Marcar resolvido
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey={(r) => `${r.customer_id}-${r.__stage.id}`}
      dataSource={rows}
      columns={columns}
      pagination={{ pageSize: 20, showSizeChanger: true }}
      size="middle"
    />
  );
};

// ────────────────────────────────────────────────────────
// Modal: marcar resolvido
// ────────────────────────────────────────────────────────

const ResolveModal = ({ open, row, stage, onCancel, onDone }) => {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setDescription('');
  }, [open]);

  const submit = async () => {
    if (!row || !stage) return;
    setLoading(true);
    try {
      await resolveOnboardingStage({
        customer_id: row.customer_id,
        stage,
        description: description.trim() || undefined,
      });
      antdMessage.success('Marcado como resolvido');
      onDone();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Falha ao resolver');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={row ? `Marcar como resolvido — ${row.game_name || `#${row.customer_id}`}` : ''}
      open={open}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={loading}
      okText="Confirmar"
      cancelText="Cancelar"
    >
      <Paragraph>
        Descreva o que foi feito pra resolver. A anotação vai pro histórico de suporte do time.
        A resolução expira em <b>30 dias</b> — se o problema persistir depois disso, o alerta volta.
      </Paragraph>
      <Input.TextArea
        rows={4}
        placeholder="Ex: cliente ligou, orientou a sincronizar o grupo, verificou que funcionou"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </Modal>
  );
};

// ────────────────────────────────────────────────────────
// Modal: histórico de suporte
// ────────────────────────────────────────────────────────

const HistoryModal = ({ open, customerId, customerName, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const rows = await fetchCustomerSupportLogs(customerId);
      setLogs(rows);
    } catch {
      antdMessage.error('Não foi possível carregar histórico');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open) {
      setNote('');
      load();
    }
  }, [open, load]);

  const submit = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await addCustomerSupportLog(customerId, note.trim());
      setNote('');
      antdMessage.success('Nota adicionada');
      load();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Falha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Histórico de suporte — ${customerName || `#${customerId || ''}`}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>,
      ]}
      width={720}
    >
      <div style={{ marginBottom: 16 }}>
        <Input.TextArea
          rows={3}
          placeholder="Adicionar nota de atendimento…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Button
            type="primary"
            onClick={submit}
            loading={saving}
            disabled={!note.trim()}
          >
            Adicionar nota
          </Button>
        </div>
      </div>

      <Divider style={{ margin: '8px 0 16px' }} />

      {loading ? (
        <Skeleton active />
      ) : logs.length === 0 ? (
        <Empty description="Sem interações anteriores" />
      ) : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {logs.map((log) => (
            <div
              key={log.id}
              style={{
                padding: 12,
                marginBottom: 8,
                background: '#fafafa',
                borderRadius: 6,
                borderLeft: '3px solid #1677ff',
              }}
            >
              <Space size={8} wrap>
                <Text strong>{log.author_name || 'sistema'}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(log.created_at).toLocaleString('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                  })}
                </Text>
              </Space>
              <Paragraph style={{ marginBottom: 0, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                {log.description}
              </Paragraph>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default OnboardingAlerts;
